import { IconCheck, IconCircleX } from '@tabler/icons-react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import PoweredByFooter from '@pages/ComponentsPage/PoweredByFooter';
import { rupiah } from '@lib/currency';
import { DataDetailCheckout, SettingsProps } from '../types';

interface SuccessResultProps {
  data: DataDetailCheckout;
  settings?: SettingsProps;
}

const SuccessResult: React.FC<SuccessResultProps> = ({ data, settings }) => {
  const [countdown, setCountdown] = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const isFailedPayement =
    data?.payment?.status === 'FAILED' ||
    String(data?.status || '').toUpperCase() === 'CANCELLED';

  useEffect(() => {
    if (data?.redirect_url && !isFailedPayement) {
      setIsRedirecting(true);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = data.redirect_url;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [data?.redirect_url, isFailedPayement]);

  const dataInvoice = [
    {
      label: 'Invoice ID',
      value: data?.invoice_number ? data?.invoice_number : '-',
    },
    {
      label: 'Reference ID',
      value: data?.payment?.reference_id ? data?.payment.reference_id : '-',
    },
    {
      label: 'Merchant',
      value: data?.merchant?.name ? data?.merchant?.name : '-',
    },
    ...(data?.payment?.va?.va_number
      ? [
          {
            label: 'Virtual Account Number',
            value: data.payment.va.va_number,
          },
        ]
      : []),
    {
      label: 'Payment Method',
      value: data?.payment?.type
        ? data.payment.type === 'direct_debit'
          ? 'debit_card'
          : data.payment.type
        : '-',
    },
    {
      label: 'Date',
      value: data?.invoice_date
        ? format(new Date(data.invoice_date), 'dd MMM yyyy')
        : '-',
    },
  ];

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mt-4 flex h-fit items-center justify-center rounded-xl bg-white px-4 shadow-sm sm:px-6 lg:px-8">
        <div className="flex w-full flex-col gap-8 px-4 pb-6 pt-8 lg:px-0">
          {isFailedPayement ? (
            <div className="flex flex-col items-center gap-6">
              <div className="mx-auto w-fit">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-[#EF4444] bg-[#EF4444]">
                  <IconCircleX className="h-[76px] w-[76px] text-white" />
                </div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-3xl font-semibold text-gray-700">
                  Payment Failed
                </span>
                <span className="text-base font-normal text-gray-700">
                  Your transaction was unsuccessful.
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6">
              <div className="mx-auto w-fit">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-[#16A34A] bg-[#BBF7D0] p-4">
                  <IconCheck className="h-[76px] w-[76px] text-[#16A34A]" />
                </div>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="text-3xl font-semibold text-gray-700">
                  Payment Successful
                </span>
                <span className="text-base font-normal text-gray-700">
                  Your transaction has been received and verified successfully.
                </span>
              </div>
              {isRedirecting && data?.redirect_url && (
                <div
                  className="w-full rounded-xl shadow-sm"
                  style={{
                    backgroundColor: settings?.button_color
                      ? settings.button_color
                      : '#5D87FF',
                    color: settings?.button_text_color
                      ? settings.button_text_color
                      : '#FFFFFF',
                  }}
                >
                  <div className="mx-auto flex w-fit flex-row items-center gap-2 px-4 py-1">
                    <span>You will be redirected automatically in</span>
                    <div className="relative h-14 w-14">
                      <svg
                        className="h-full w-full animate-spin"
                        viewBox="0 0 100 100"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke={settings?.button_color || '#5D87FF'}
                          strokeWidth="8"
                          opacity="0.3"
                        />

                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke={settings?.button_text_color || '#FFFFFF'}
                          strokeWidth="8"
                          strokeDasharray="110 110"
                          strokeDashoffset="27.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span
                          className="text-base font-bold"
                          style={{
                            color: settings?.button_text_color
                              ? settings.button_text_color
                              : '#FFFFFF',
                          }}
                        >
                          {countdown}
                        </span>
                      </div>
                    </div>
                    <span>Seconds</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-8 text-center">
            <div>
              {dataInvoice.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex flex-row items-center justify-between border-b border-gray-200 py-3`}
                >
                  <span className="text-sm font-medium text-neutral-500">
                    {item.label}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {item.value}
                  </span>
                </div>
              ))}

              <div
                className={`flex flex-row items-center justify-between pt-6`}
              >
                <span className="text-sm font-medium text-neutral-500">
                  Amount
                </span>
                <span
                  className="text-2xl font-semibold"
                  style={{
                    color: isFailedPayement
                      ? '#000000'
                      : settings?.button_color || '#000000',
                  }}
                >
                  {data?.total ? rupiah(data?.total) : 'Rp -'}
                </span>
              </div>
            </div>

            {!isFailedPayement && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-row items-center justify-between gap-4">
                  {/* <button
                    onClick={() => {
                      console.log('Download Receipt');
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-[#FAFAFA] px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Download Receipt
                  </button>
                  <button
                    onClick={() => {
                      console.log('Send Email');
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-[#FAFAFA] px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Send Email
                  </button> */}
                </div>

                {/* <button
                  onClick={() => {
                    if (data?.redirect_url) {
                      window.location.href = data.redirect_url;
                    }
                  }}
                  className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: settings?.button_color
                      ? settings.button_color
                      : '#5D87FF',
                    color: settings?.button_text_color
                      ? settings.button_text_color
                      : '#FFFFFF',
                  }}
                >
                  Redirect to Merchant
                </button> */}
              </div>
            )}

            <div className="flex flex-row items-center justify-between pb-6">
              <div className="flex flex-row items-center justify-center gap-2 text-sm text-gray-500">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 11H6C6.53043 11 7.03914 11.2107 7.41421 11.5858C7.78929 11.9609 8 12.4696 8 13V16C8 16.5304 7.78929 17.0391 7.41421 17.4142C7.03914 17.7893 6.53043 18 6 18H5C4.46957 18 3.96086 17.7893 3.58579 17.4142C3.21071 17.0391 3 16.5304 3 16V11ZM3 11C3 9.8181 3.23279 8.64778 3.68508 7.55585C4.13738 6.46392 4.80031 5.47177 5.63604 4.63604C6.47177 3.80031 7.46392 3.13738 8.55585 2.68508C9.64778 2.23279 10.8181 2 12 2C13.1819 2 14.3522 2.23279 15.4442 2.68508C16.5361 3.13738 17.5282 3.80031 18.364 4.63604C19.1997 5.47177 19.8626 6.46392 20.3149 7.55585C20.7672 8.64778 21 9.8181 21 11M21 11V16C21 16.5304 20.7893 17.0391 20.4142 17.4142C20.0391 17.7893 19.5304 18 19 18H18C17.4696 18 16.9609 17.7893 16.5858 17.4142C16.2107 17.0391 16 16.5304 16 16V13C16 12.4696 16.2107 11.9609 16.5858 11.5858C16.9609 11.2107 17.4696 11 18 11H21Z"
                    stroke="#525252"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 16V18C21 19.0609 20.5786 20.0783 19.8284 20.8284C19.0783 21.5786 18.0609 22 17 22H12"
                    stroke="#525252"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-light text-[#404040]">
                  Need help?{' '}
                  <span className="font-semibold text-black">
                    Contact Support
                  </span>
                </span>
              </div>

              <div className="-mt-4">
                <PoweredByFooter textSecurePayment={false} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessResult;
